// Structural (JSON/YAML) render. Side-by-side is a normalized-order PATH LEDGER
// (path · left value | right value), aligned by path so key reordering never
// shows as a change. Unified is a compact change list; Raw is the line-based
// fallback (the teaching moment).
import { renderStructuredSource } from './structured-source-view.js';
import { inlineDiff } from '../engine/inline-diff.js';
import { esc } from './escape.js';

const line = (n, t, cls = '', path = null) =>
  `<div class="code-line ${cls}"${path == null ? '' : ` data-path="${esc(String(path))}"`}><span class="ln">${cls === 'del' ? '−' : cls === 'add' ? '+' : n}</span><span class="code">${t || ' '}</span></div>`;
const val = (v) => esc(JSON.stringify(v));
const cap = (t) => `<div class="cap" style="margin-left:16px">${t}</div>`;

// Which ledger rows a category filter keeps. `type` counts as a change AND as a
// type change, because it is one.
const FILTER_STATUS = { added: ['add'], removed: ['remove'], changed: ['change', 'type'], typeChanges: ['type'] };

export function renderStructural(result, view, meta = {}, { filter = null } = {}) {
  // Filtering shows the PATH LEDGER: the source view is the two documents as
  // they were written, and hiding lines out of a document would misrepresent
  // the file rather than narrow a list.
  if (view === 'sxs' && result.render.source && !filter) return renderStructuredSource(result, meta);
  if (view === 'raw') {
    const rows = result.render.rawRows.map((r) => line(r.ln, esc(r.text), r.cls)).join('');
    return { html: rows + cap('Raw line view — key reordering makes line-based tools report far more “changes” than there are.'), ribbons: [] };
  }
  if (view === 'unified' || !result.render.rows) return { html: unifiedList(result, filter), ribbons: [] };
  return { html: sideBySide(result, meta, filter), ribbons: [] };
}

function editedValue(oldValue, newValue, side) {
  return inlineDiff(JSON.stringify(oldValue) ?? '', JSON.stringify(newValue) ?? '', 'word')
    .filter(part => part.type !== (side === 'old' ? 'new' : 'old'))
    .map(part => part.type === 'ctx' ? esc(part.text) : `<span class="${side === 'old' ? 'hlOld' : 'hlNew'}">${esc(part.text)}</span>`).join('');
}

function sideBySide(result, meta, filter = null) {
  const leftLabel = esc(meta.leftLabel || 'Original');
  const rightLabel = esc(meta.rightLabel || 'Changed');
  let h = `<div class="sxs" style="grid-template-columns:1fr 34px 1fr"><div class="sxs-hdr" style="grid-template-columns:subgrid"><span class="h">${leftLabel} · normalized order</span><span></span><span class="h">${rightLabel} · normalized order</span></div>`;

  const kept = filter ? (FILTER_STATUS[filter] || []) : null;
  let hidden = 0;
  for (const r of result.render.rows) {
    if (kept && !kept.includes(r.status)) { if (r.status !== 'context') hidden++; continue; }
    const p = esc(r.path);
    if (r.status === 'context') h += srow(`${p}: ${val(r.oldValue)}`, 'ctx', `${p}: ${val(r.newValue)}`, 'ctx', r.path);
    else if (r.status === 'change') h += srow(`${p}: ${editedValue(r.oldValue, r.newValue, 'old')}`, 'del', `${p}: ${editedValue(r.oldValue, r.newValue, 'new')}`, 'add', r.path);
    else if (r.status === 'type') h += srow(`${p}: ${editedValue(r.oldValue, r.newValue, 'old')}`, 'del', `${p}: ${editedValue(r.oldValue, r.newValue, 'new')}<span class="warnb">${esc(r.oldType)} → ${esc(r.newType)}</span>`, 'add', r.path);
    else if (r.status === 'add') h += srow(' ', 'sp', `${p}: ${val(r.newValue)}`, 'add', r.path);
    else if (r.status === 'remove') h += srow(`${p}: ${val(r.oldValue)}`, 'del', ' ', 'sp', r.path);
  }
  h += cap(filter
    ? `Filtered to <b>${esc(filter === 'typeChanges' ? 'type changes' : filter)}</b>${hidden ? ` — ${hidden} other changed ${hidden === 1 ? 'field is' : 'fields are'} hidden` : ''}. Nothing was truncated; use <b>Show all changes</b> to see them.`
    : 'Panes shown in normalized key order — the reordering in the raw files isn’t a change, so it isn’t shown as one. Switch to Raw to see what line-based tools make of it.');
  return `${h}</div>`;
}

// `path` is the address the change navigator resolves a stop to; the renderer
// only has to publish the coordinate it already knows.
function srow(l, lc, r, rc, path = null) {
  const at = path == null ? '' : ` data-path="${esc(String(path))}"`;
  return `<div class="sxs-row" style="grid-template-columns:subgrid"${at}><span class="c ${lc}">${l}</span><span class="gut"></span><span class="c ${rc}">${r}</span></div>`;
}

// Every row of the compact list is addressable by its path.
function lineFor(change, html, cls) {
  return line('', html, cls, change.path);
}

function unifiedList(result, filter = null) {
  let h = '';
  const kept = filter ? (FILTER_STATUS[filter] || []) : null;
  let hidden = 0;
  for (const c of result.render.changes) {
    if (kept && !kept.includes(c.kind)) { hidden++; continue; }
    const path = esc(displayPath(c));
    if (c.kind === 'reorder') h += line('', '<span style="color:var(--faint)">ignored  key order changed — not a change</span>', '');
    else if (c.kind === 'type') h += lineFor(c, `<b style="color:var(--amber)">type ⚠</b>   <b>${path}</b>: <span class="edit-old">${val(c.oldValue)}</span> → <span class="edit-new">${val(c.newValue)}</span> <span style="color:var(--faint)">(${esc(c.oldType)} → ${esc(c.newType)})</span>`, 'chg');
    else if (c.kind === 'change') h += lineFor(c, `<b style="color:var(--chg)">changed</b>  <b>${path}</b>: ${editedValue(c.oldValue, c.newValue, 'old')} → ${editedValue(c.oldValue, c.newValue, 'new')}`, 'chg');
    else if (c.kind === 'add') h += lineFor(c, `<b style="color:var(--add)">added</b>    <b>${path}</b>: ${val(c.newValue)}`, 'add');
    else if (c.kind === 'remove') h += lineFor(c, `<b style="color:var(--del)">removed</b>  <b>${path}</b>`, 'del');
  }
  if (!h) {
    return cap(filter
      ? 'No differences of this kind — use <b>Show all changes</b> to see the rest.'
      : 'No structural differences — the documents are equivalent (key order aside).');
  }
  return h + cap(filter
    ? `Filtered to <b>${esc(filter === 'typeChanges' ? 'type changes' : filter)}</b>${hidden ? ` — ${hidden} other ${hidden === 1 ? 'difference is' : 'differences are'} hidden` : ''}. Nothing was truncated; use <b>Show all changes</b> to see them.`
    : 'Compact change list — every difference, keyed by path.');
}

function displayPath(change) {
  const positional = typeof change.recordKey === 'string' ? change.recordKey.match(/^#(\d+)$/) : null;
  if (!positional) return change.path;
  return change.path === '$' ? `[${positional[1]}]` : `[${positional[1]}].${change.path}`;
}
